import React, { useEffect, useRef, useState } from 'react';
import './Report.css';

const DAY_MS = 86400000;
const PRESETS = [7, 30, 90];

// The report works in UTC days, so ranges are built in UTC as well.
const isoDay = (date) => date.toISOString().slice(0, 10);
export const lastDays = (n) => {
  const now = Date.now();
  return { from: isoDay(new Date(now - (n - 1) * DAY_MS)), to: isoDay(new Date(now)) };
};

export const formatMoney = (value, currencyKey) => {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currencyKey }).format(value);
  } catch {
    return value.toFixed(2);
  }
};

// Custom dropdown: a native <select> list is drawn by the browser (outside our CSS) and can be
// mispositioned, so the list is rendered by the page itself.
const RestaurantSelect = ({ restaurants, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (e.type === 'keydown' ? e.key === 'Escape' : !ref.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);

  return (
    <div className="report-select" ref={ref}>
      <button type="button" className="report-input report-select-btn" aria-haspopup="listbox"
        aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span>{restaurants.find((r) => r.id === value)?.name}</span>
        <span aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="report-select-list" role="listbox">
          {restaurants.map((r) => (
            <button
              key={r.id}
              type="button"
              role="option"
              aria-selected={r.id === value}
              className={`report-preset${r.id === value ? ' is-active' : ''}`}
              onClick={() => { onChange(r.id); setOpen(false); }}
            >
              {r.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const ReportPage = ({ report, children }) => {
  const { data, error, validRange, filters } = report;
  const { restaurants, restaurantId, onRestaurantChange, range, onRangeChange } = filters;

  return (
    <div className="report">
      <div className="report-controls">
        {restaurants.length > 1 && (
          <RestaurantSelect restaurants={restaurants} value={restaurantId} onChange={onRestaurantChange} />
        )}
        <div className="report-presets">
          {PRESETS.map((days) => (
            <button key={days} type="button" className="report-preset" onClick={() => onRangeChange(lastDays(days))}>
              {days} days
            </button>
          ))}
        </div>
        <input className="report-input" type="date" aria-label="From" value={range.from} max={range.to}
          onChange={(e) => onRangeChange((r) => ({ ...r, from: e.target.value }))} />
        <input className="report-input" type="date" aria-label="To" value={range.to} min={range.from}
          onChange={(e) => onRangeChange((r) => ({ ...r, to: e.target.value }))} />
      </div>
      <p className="report-note">Days are counted in UTC. Only completed orders are included.</p>

      {!validRange && <div className="settings-error">Choose a valid date range.</div>}
      {error && <div className="settings-error">⚠️ {error}</div>}
      {validRange && !error && !data && <p className="settings-loading">Loading...</p>}
      {data && data.length === 0 && <p className="settings-loading">No completed orders in this period.</p>}
      {data && data.length > 0 && children(data)}
    </div>
  );
};
